import React from 'react';
import type { QuickMessageContent } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { Tooltip } from '@/shared/components/ui/Tooltip';

interface QuickMessageStagedPreviewProps {
  /** Media parts (already hosted on S3) staged from a quick message. */
  media: QuickMessageContent[];
  /** Clear the staged media. */
  onClear: () => void;
}

/**
 * Preview of media staged into the composer from a quick message. The media is
 * already uploaded (S3 URLs) — sending reuses those URLs via `/send`.
 */
const QuickMessageStagedPreview: React.FC<QuickMessageStagedPreviewProps> = ({ media, onClear }) => {
  if (media.length === 0) return null;

  const renderItem = (item: QuickMessageContent, index: number) => {
    if (item.type === 'image') {
      return (
        <div
          key={index}
          className="w-16 h-16 rounded-lg overflow-hidden border flex-shrink-0"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <img src={item.url} alt="" className="w-full h-full object-cover" />
        </div>
      );
    }

    const iconName = item.type === 'video' ? 'video' : item.type === 'audio' ? 'music' : 'file';
    const label =
      item.type === 'video'
        ? 'Vídeo'
        : item.type === 'audio'
          ? 'Áudio'
          : item.type === 'document'
            ? item.filename
            : '';

    return (
      <div
        key={index}
        className="h-16 px-3 rounded-lg border flex items-center gap-2 flex-shrink-0"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <Icon name={iconName} className="w-5 h-5" style={{ color: 'var(--theme-sidebar-active)' }} />
        <Tooltip content={label}>
          <span
            className="text-xs font-medium max-w-[120px] truncate"
            style={{ color: 'var(--theme-card-text)' }}
          >
            {label}
          </span>
        </Tooltip>
      </div>
    );
  };

  return (
    <div
      className="flex-shrink-0 px-4 py-2 border-t"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}
      data-testid="quick-message-staged-preview"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon name="zap" className="w-3.5 h-3.5" style={{ color: 'var(--theme-sidebar-active)' }} />
          <span className="text-xs font-medium" style={{ color: 'var(--theme-muted-text)' }}>
            Mídia da mensagem rápida
          </span>
        </div>
        <Tooltip content="Remover mídia">
          <button
            onClick={onClear}
            className="p-1 rounded-md transition-colors"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              e.currentTarget.style.color = 'var(--theme-card-text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--theme-muted-text)';
            }}
            data-testid="quick-message-staged-clear"
          >
            <Icon name="x" className="w-4 h-4" />
          </button>
        </Tooltip>
      </div>
      <div className="flex gap-2 overflow-x-auto">{media.map(renderItem)}</div>
    </div>
  );
};

QuickMessageStagedPreview.displayName = 'QuickMessageStagedPreview';

export default QuickMessageStagedPreview;
